// Re-export API types with cleaner names
import type {
  workflows_WorkflowItemResponse,
  workflows_WorkflowItemDetailResponse,
  workflows_WorkflowItemListResponse,
  workflows_TransitionRequest,
  workflows_PendingCountResponse,
  workflows_WorkflowTransitionResponse,
} from '@/api';
import type { StatusTone } from '@/components/ui/status-badge';

export type WorkflowItemResponse = workflows_WorkflowItemResponse;
export type WorkflowItemDetailResponse = workflows_WorkflowItemDetailResponse;
export type WorkflowItemListResponse = workflows_WorkflowItemListResponse;
export type TransitionRequest = workflows_TransitionRequest;
export type PendingCountResponse = workflows_PendingCountResponse;
export type WorkflowTransitionResponse = workflows_WorkflowTransitionResponse;

// Workflow category type
export type WorkflowCategory =
  | 'accident_notification'
  | 'child_data_update'
  | 'leave_request'
  | 'timesheet_approval'
  | 'dunning'
  | 'contract_termination'
  | 'change_request'
  | 'pickup_exception'
  | 'portfolio_full_record_request'
  | 'portfolio_conversation_due'
  | 'split_alert'
  | 'subsidy_report'
  | 'subsidy_expiry'
  | 'late_extras'
  | 'shift_request'
  | 'coverage_breach'
  | 'diaper_check_overdue'
  | 'activity_gap';

// States and actions vary by category — use string
export type WorkflowState = string;
export type WorkflowAction = string;

// Category constants
export const WORKFLOW_CATEGORIES = [
  'accident_notification',
  'child_data_update',
  'leave_request',
  'timesheet_approval',
  'dunning',
  'contract_termination',
  'change_request',
  'pickup_exception',
  'portfolio_full_record_request',
  'portfolio_conversation_due',
  'split_alert',
  'subsidy_report',
  'subsidy_expiry',
  'late_extras',
  'shift_request',
  'coverage_breach',
  'diaper_check_overdue',
  'activity_gap',
] as const;

/**
 * Care alerts share one FSM: pending -> acknowledged | dismissed (both need a
 * comment), plus resolved by the system only (auto_resolve). A new kind is one
 * entry here, one in CARE_ALERT_COPY and one in CARE_ALERT_SUMMARY.
 */
export const CARE_ALERT_CATEGORIES = ['diaper_check_overdue', 'activity_gap'] as const;
export type CareAlertCategory = (typeof CARE_ALERT_CATEGORIES)[number];

export function isCareAlertCategory(category: string | undefined): category is CareAlertCategory {
  return (CARE_ALERT_CATEGORIES as readonly string[]).includes(category ?? '');
}

type Translate = { _: (descriptor: { id: string; message: string }) => string };

/** Per-kind copy of the shared care-alert behaviour. Explicit ids so Lingui extracts every literal. */
export const CARE_ALERT_COPY: Record<
  CareAlertCategory,
  {
    acknowledgeSummary: (i18n: Translate) => string;
    dismissSummary: (i18n: Translate) => string;
    autoResolveLabel: (i18n: Translate) => string;
  }
> = {
  diaper_check_overdue: {
    acknowledgeSummary: (i18n) =>
      i18n._({
        id: 'workflow.transition.diaperCheck.acknowledgeSummary',
        message: 'You followed up on the diaper check. The item will be closed.',
      }),
    dismissSummary: (i18n) =>
      i18n._({
        id: 'workflow.transition.diaperCheck.dismissSummary',
        message: 'The gap is known, for example a change that was not recorded. The item will be closed.',
      }),
    autoResolveLabel: (i18n) =>
      i18n._({ id: 'workflow.action.auto_resolve', message: 'Resolved automatically: toilet entry recorded' }),
  },
  activity_gap: {
    acknowledgeSummary: (i18n) =>
      i18n._({
        id: 'workflow.transition.activityGap.acknowledgeSummary',
        message: 'You followed up on the missing entries. The item will be closed.',
      }),
    dismissSummary: (i18n) =>
      i18n._({
        id: 'workflow.transition.activityGap.dismissSummary',
        message: 'The gap is known, for example an outing. The item will be closed.',
      }),
    autoResolveLabel: (i18n) =>
      i18n._({ id: 'workflow.action.autoResolveEntry', message: 'Resolved automatically: entry recorded' }),
  },
};

// State constants (accident_notification states)
export const WORKFLOW_STATES = [
  'open',
  'acknowledged',
  'escalated',
  'parents_notified',
  'closed',
] as const;

// The full set of workflow states across all categories, mirroring the cases in
// `getStateLabel`. Used by static analytics dropdowns so the filter offers every
// state independent of which happen to be on the loaded page.
export const WORKFLOW_ALL_STATES = [
  'open',
  'acknowledged',
  'escalated',
  'parents_notified',
  'closed',
  'reminder_1_sent',
  'reminder_2_sent',
  'reminder_3_sent',
  'final_notice_sent',
  'paid',
  'collections_handover',
  'cancelled',
  'requested',
  'approved',
  'completed',
  'rejected',
  // contract_termination: a completed termination that was revoked (#1678).
  'revoked',
  'current',
  // Initial state of the notice categories pickup_exception,
  // portfolio_conversation_due, portfolio_full_record_request and split_alert
  // ('acknowledged' is already listed above).
  'pending',
  // subsidy_report (#2003): to_file -> filed -> approved | rejected.
  'to_file',
  'filed',
  // late_extras (#2028): pending -> regenerated | skipped.
  'regenerated',
  'skipped',
  // coverage_breach, diaper_check_overdue and activity_gap: pending -> acknowledged | dismissed
  // (diaper_check_overdue and activity_gap also -> resolved, by the system only).
  'dismissed',
] as const;

// Action display labels (English fallbacks — actual translations via i18n helpers below)
export const ACTION_LABELS: Record<string, string> = {
  acknowledge: 'Acknowledge',
  confirm_parents_notified: 'Confirm Parents Notified',
  close: 'Close',
  escalate: 'Escalate',
};

/**
 * i18n-safe label helpers. Using explicit IDs so Lingui extracts them.
 * Pass the i18n instance from useLingui().
 */
// category is only needed to name what closed a care alert item by itself (auto_resolve).
export function getActionLabel(i18n: { _: (descriptor: { id: string; message: string }) => string }, action: string, category?: string): string {
  switch (action) {
    case 'acknowledge': return i18n._({ id: 'workflow.action.acknowledge', message: 'Acknowledge' });
    case 'confirm_parents_notified': return i18n._({ id: 'workflow.action.confirm_parents_notified', message: 'Confirm Parents Notified' });
    case 'close': return i18n._({ id: 'workflow.action.close', message: 'Close' });
    case 'escalate': return i18n._({ id: 'workflow.action.escalate', message: 'Escalate' });
    case 'send_reminder_1': return i18n._({ id: 'workflow.action.send_reminder_1', message: 'Send reminder 1' });
    case 'send_reminder_2': return i18n._({ id: 'workflow.action.send_reminder_2', message: 'Send reminder 2' });
    case 'send_reminder_3': return i18n._({ id: 'workflow.action.send_reminder_3', message: 'Send reminder 3' });
    case 'send_final_notice': return i18n._({ id: 'workflow.action.send_final_notice', message: 'Send final notice' });
    case 'send_final_repeat': return i18n._({ id: 'workflow.action.send_final_repeat', message: 'Repeat final notice' });
    case 'send_now': return i18n._({ id: 'workflow.action.send_now', message: 'Send next reminder now' });
    case 'mark_paid': return i18n._({ id: 'workflow.action.mark_paid', message: 'Mark as paid' });
    case 'mark_handover': return i18n._({ id: 'workflow.action.mark_handover', message: 'Mark collections handover' });
    case 'cancel': return i18n._({ id: 'workflow.action.cancel', message: 'Cancel' });
    case 'pause': return i18n._({ id: 'workflow.action.pause', message: 'Pause' });
    case 'resume': return i18n._({ id: 'workflow.action.resume', message: 'Resume' });
    case 'approve': return i18n._({ id: 'workflow.action.approve', message: 'Approve' });
    case 'reject': return i18n._({ id: 'workflow.action.reject', message: 'Reject' });
    case 'process': return i18n._({ id: 'workflow.action.process', message: 'Process' });
    case 'revoke': return i18n._({ id: 'workflow.action.revoke', message: 'Revoke' });
    case 'resolve': return i18n._({ id: 'workflow.action.resolve', message: 'Mark resolved' });
    case 'mark_manual': return i18n._({ id: 'workflow.action.mark_manual', message: 'Mark for manual handling' });
    case 'execute': return i18n._({ id: 'workflow.action.execute', message: 'Apply' });
    // Only the parent fires it (parent app); staff see it in the history.
    case 'withdraw': return i18n._({ id: 'workflow.action.withdraw', message: 'Withdrawn by the parent' });
    // subsidy_report (#2003): fired from the item's own dialogs; the labels show in the history.
    case 'file': return i18n._({ id: 'workflow.action.file', message: 'Marked as filed' });
    case 'record_approval': return i18n._({ id: 'workflow.action.record_approval', message: 'Approval recorded' });
    case 'record_rejection': return i18n._({ id: 'workflow.action.record_rejection', message: 'Rejection recorded' });
    // late_extras (#2028): regenerate fires from the item's own button; skip is a plain transition.
    case 'regenerate': return i18n._({ id: 'workflow.action.regenerate', message: 'Invoices re-issued' });
    // coverage_breach, diaper_check_overdue and activity_gap: both exits need a comment.
    case 'dismiss': return i18n._({ id: 'workflow.action.dismiss', message: 'Dismiss' });
    case 'skip': return i18n._({ id: 'workflow.action.skip', message: 'Skip' });
    // Assignment rows in the timeline (the assign route, not FSM actions).
    case 'assign': return i18n._({ id: 'workflow.action.assign', message: 'Handed over' });
    case 'unassign': return i18n._({ id: 'workflow.action.unassign', message: 'Person removed' });
    // diaper_check_overdue and activity_gap: system rows in the history.
    // portfolio_conversation_due: closed when a talk is planned or recorded.
    case 'auto_resolve':
      if (category === 'portfolio_conversation_due') {
        return i18n._({
          id: 'workflow.action.autoResolveConversation',
          message: 'Closed automatically: a development talk was planned or recorded',
        });
      }
      return CARE_ALERT_COPY[isCareAlertCategory(category) ? category : 'diaper_check_overdue'].autoResolveLabel(i18n);
    case 'hand_to_admins': return i18n._({ id: 'workflow.action.hand_to_admins', message: 'Handed to the administration (deadline passed)' });
    default: return action;
  }
}

export function getCategoryLabel(i18n: { _: (descriptor: { id: string; message: string }) => string }, category: string): string {
  switch (category) {
    case 'accident_notification': return i18n._({ id: 'workflow.category.accident_notification', message: 'Accident Notification' });
    case 'child_data_update': return i18n._({ id: 'workflow.category.child_data_update', message: 'Child Data Update' });
    case 'leave_request': return i18n._({ id: 'workflow.category.leave_request', message: 'Leave Request' });
    case 'timesheet_approval': return i18n._({ id: 'workflow.category.timesheet_approval', message: 'Timesheet Approval' });
    case 'dunning': return i18n._({ id: 'workflow.category.dunning', message: 'Dunning' });
    case 'contract_termination': return i18n._({ id: 'workflow.category.contract_termination', message: 'Contract Termination' });
    case 'change_request': return i18n._({ id: 'workflow.category.change_request', message: 'Change Request' });
    case 'pickup_exception': return i18n._({ id: 'workflow.category.pickup_exception', message: 'Pickup Exception' });
    case 'portfolio_full_record_request': return i18n._({ id: 'workflow.category.portfolio_full_record_request', message: 'Data access request' });
    case 'portfolio_conversation_due': return i18n._({ id: 'workflow.category.portfolio_conversation_due', message: 'Development Talk Due' });
    case 'split_alert': return i18n._({ id: 'workflow.category.split_alert', message: 'Group Split Alert' });
    case 'subsidy_report': return i18n._({ id: 'workflow.category.subsidy_report', message: 'Subsidy report' });
    case 'subsidy_expiry': return i18n._({ id: 'workflow.category.subsidy_expiry', message: 'Expiry reminder' });
    case 'late_extras': return i18n._({ id: 'workflow.category.late_extras', message: 'Extras after invoicing' });
    case 'shift_request': return i18n._({ id: 'workflow.category.shift_request', message: 'Shift request' });
    case 'coverage_breach': return i18n._({ id: 'workflow.category.coverage_breach', message: 'Care ratio not met' });
    case 'diaper_check_overdue': return i18n._({ id: 'workflow.category.diaper_check_overdue', message: 'Diaper check overdue' });
    case 'activity_gap': return i18n._({ id: 'workflow.category.activity_gap', message: 'Activity gap' });
    default: return category;
  }
}

export function getStateLabel(i18n: { _: (descriptor: { id: string; message: string }) => string }, state: string): string {
  switch (state) {
    case 'open': return i18n._({ id: 'workflow.state.open', message: 'Open' });
    case 'acknowledged': return i18n._({ id: 'workflow.state.acknowledged', message: 'Acknowledged' });
    case 'escalated': return i18n._({ id: 'workflow.state.escalated', message: 'Escalated' });
    case 'parents_notified': return i18n._({ id: 'workflow.state.parents_notified', message: 'Parents Notified' });
    case 'closed': return i18n._({ id: 'workflow.state.closed', message: 'Closed' });
    case 'current': return i18n._({ id: 'workflow.state.current', message: 'Current' });
    case 'reminder_1_sent': return i18n._({ id: 'workflow.state.reminder_1_sent', message: 'Reminder 1 sent' });
    case 'reminder_2_sent': return i18n._({ id: 'workflow.state.reminder_2_sent', message: 'Reminder 2 sent' });
    case 'reminder_3_sent': return i18n._({ id: 'workflow.state.reminder_3_sent', message: 'Reminder 3 sent' });
    case 'final_notice_sent': return i18n._({ id: 'workflow.state.final_notice_sent', message: 'Final notice sent' });
    case 'paid': return i18n._({ id: 'workflow.state.paid', message: 'Paid' });
    case 'collections_handover': return i18n._({ id: 'workflow.state.collections_handover', message: 'Collections handover' });
    case 'cancelled': return i18n._({ id: 'workflow.state.cancelled', message: 'Cancelled' });
    case 'requested': return i18n._({ id: 'workflow.state.requested', message: 'Requested' });
    case 'approved': return i18n._({ id: 'workflow.state.approved', message: 'Approved' });
    case 'completed': return i18n._({ id: 'workflow.state.completed', message: 'Completed' });
    case 'rejected': return i18n._({ id: 'workflow.state.rejected', message: 'Rejected' });
    case 'revoked': return i18n._({ id: 'workflow.state.revoked', message: 'Revoked' });
    case 'pending': return i18n._({ id: 'workflow.state.pending', message: 'Pending' });
    case 'executed': return i18n._({ id: 'workflow.state.executed', message: 'Executed' });
    case 'failed': return i18n._({ id: 'workflow.state.failed', message: 'Failed' });
    case 'manual': return i18n._({ id: 'workflow.state.manual', message: 'Needs human handling' });
    case 'resolved': return i18n._({ id: 'workflow.state.resolved', message: 'Resolved' });
    case 'to_file': return i18n._({ id: 'workflow.state.to_file', message: 'To file' });
    case 'filed': return i18n._({ id: 'workflow.state.filed', message: 'Filed' });
    case 'regenerated': return i18n._({ id: 'workflow.state.regenerated', message: 'Invoices re-issued' });
    case 'skipped': return i18n._({ id: 'workflow.state.skipped', message: 'Skipped' });
    case 'dismissed': return i18n._({ id: 'workflow.state.dismissed', message: 'Dismissed' });
    default: return state;
  }
}

// State transition mapping for preview
export const STATE_TRANSITION_MAP: Record<string, string> = {
  acknowledge: 'acknowledged',
  confirm_parents_notified: 'parents_notified',
  close: 'closed',
  escalate: 'escalated',
  approve: 'approved',
  reject: 'rejected',
  process: 'completed',
  revoke: 'revoked',
  cancel: 'cancelled',
  resolve: 'resolved',
  execute: 'executed',
  mark_manual: 'manual',
  withdraw: 'cancelled',
  file: 'filed',
  record_approval: 'approved',
  record_rejection: 'rejected',
  regenerate: 'regenerated',
  skip: 'skipped',
  dismiss: 'dismissed',
};

/**
 * The status tone of a workflow state for `StatusBadge`: open work stays neutral,
 * states that need attention warn, finished states read as success.
 */
export function getStateTone(state?: string): StatusTone {
  switch (state) {
    case 'escalated':
    case 'rejected':
    case 'failed':
      return 'danger';
    case 'parents_notified':
    case 'manual':
      return 'warning';
    case 'acknowledged':
    case 'approved':
    case 'filed':
      return 'info';
    case 'closed':
    case 'completed':
    case 'executed':
    case 'resolved':
    case 'regenerated':
      return 'success';
    default:
      return 'neutral';
  }
}

/**
 * Urgency level based on due_at timestamp.
 */
export type UrgencyLevel = 'overdue' | 'urgent' | 'normal';

export function getUrgency(dueAt?: string): UrgencyLevel {
  if (!dueAt) return 'normal';
  const due = new Date(dueAt);
  const now = new Date();
  if (due < now) return 'overdue';
  if (due.getTime() - now.getTime() < 3600000) return 'urgent';
  return 'normal';
}

/**
 * Returns true if the item's due_at is today.
 */
export function isDueToday(dueAt?: string): boolean {
  if (!dueAt) return false;
  const due = new Date(dueAt);
  const now = new Date();
  return due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate();
}

/**
 * One colour per category (fill plus icon colour, token classes) for the
 * category dot and avatar. Decorative: the category label always sits next to it.
 */
export const CATEGORY_COLOR_CLASSES: Record<string, string> = {
  accident_notification: 'bg-destructive text-destructive-foreground',
  child_data_update: 'bg-info text-info-foreground',
  leave_request: 'bg-[hsl(var(--chart-6))] text-white',
  timesheet_approval: 'bg-warning text-warning-foreground',
  contract_termination: 'bg-[hsl(var(--chart-3))] text-white',
  change_request: 'bg-[hsl(var(--chart-2))] text-white',
  pickup_exception: 'bg-[hsl(var(--chart-5))] text-white',
  // Distinct from every value above: a statutory data-access request must not
  // read as a pickup escalation at a glance.
  portfolio_full_record_request: 'bg-[hsl(var(--chart-1))] text-white',
  portfolio_conversation_due: 'bg-success text-success-foreground',
  split_alert: 'bg-muted-foreground text-background',
  subsidy_report: 'bg-[hsl(var(--chart-4))] text-white',
  subsidy_expiry: 'bg-secondary text-secondary-foreground',
  // Billing work, kept apart from the subsidy colours above.
  late_extras: 'bg-foreground text-background',
  // Staff planning: the primary tone, which no other category uses.
  shift_request: 'bg-primary text-primary-foreground',
  // An alert about missing staff: the destructive tone, shared with accidents.
  coverage_breach: 'bg-destructive-muted text-destructive-on-surface',
  // Child care alert: the warning tone, apart from the staffing alert's destructive one.
  diaper_check_overdue: 'bg-warning-muted text-warning-on-surface',
  // Child care alert, apart from the diaper check's warning tone (colours stay unique per category).
  activity_gap: 'bg-info-muted text-info-on-surface',
};

export function getCategoryColorClass(category?: string): string {
  return CATEGORY_COLOR_CLASSES[category ?? ''] ?? 'bg-primary text-primary-foreground';
}

// Filter params interface
export interface WorkflowItemListParams {
  limit?: number;
  offset?: number;
  category?: string;
  state?: string;
  assignedRole?: string;
  assignedTo?: string;
  /**
   * When true, fetch the COMPLETE result set (paginating through the server's
   * 1000-row max page size) instead of a single page. Used by the board + the
   * change_request triage scope, which group/filter client-side and must not
   * silently truncate the tail.
   */
  fetchAll?: boolean;
  /** Only items needing attention (the pending badge's rule). */
  pending?: boolean;
  /** Assigned to me; for an admin also the unassigned admin queue. */
  mine?: boolean;
  /** Open items whose deadline has passed. */
  overdue?: boolean;
  /** Open items due today in the organisation's time zone. */
  dueToday?: boolean;
  /** Items no person holds. */
  unassigned?: boolean;
  /** Completed items (the done view). */
  completed?: boolean;
  /** Search text: the server matches the child, the summary and the employee names. */
  q?: string;
  /** Server sort; undefined is newest first. */
  sort?: WorkflowSort;
}

/** The sort orders the list endpoint understands. */
export type WorkflowSort = 'due_asc' | 'due_desc' | 'created_asc';

/**
 * The free-text summary of a payload. Child-bound notices (a development talk
 * due, a pickup exception, a full-record request) carry no summary, only the
 * child's name; the category column says the rest.
 */
export function payloadSummary(payload: Record<string, unknown> | undefined): string | undefined {
  const summary = payload?.summary;
  if (typeof summary === 'string' && summary) return summary;
  const childName = payload?.child_name;
  return typeof childName === 'string' && childName ? childName : undefined;
}
